import { makeStyles, Typography, useIsMobile } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { EventPreviewPopper, useUsers, useDialogs } from '@paintscout/ui-business'
import React, { useEffect, useRef, useState } from 'react'
import type { EventProps } from 'react-big-calendar'
import moment from 'moment'
import type { CalendarEvent } from 'paintscout'
import { useCancelEventFlow } from 'src/hooks'
import history from 'shared/util/routing/history'
import classnames from 'classnames'
import { formatName } from '@paintscout/util/builder'
import ReactDOM from 'react-dom'
import { getEventLabel } from '@paintscout/util'
import { EventDialog } from '../../../dialogs'
import { useCalendar } from '../CalendarContext'
import { GOOGLE_CALENDAR_EVENT_TYPE } from '../calendarUtils'
import type { CrewPalette } from '../../ProductionCalendar/util'
import { getCrewPalette } from '../../ProductionCalendar/util'
import { getUnavailableStripeBackground, unavailableBlurOverlay } from '../unavailableEventStyles'

interface WeekEventStyleProps extends CrewPalette {
  miniEvent?: boolean
  isXs?: boolean
}

const useStyles = makeStyles<Theme, WeekEventStyleProps>((theme) => ({
  root: {
    boxSizing: 'border-box',
    height: 'calc(100% - 6px)',
    width: 'calc(100% - 8px)',
    marginTop: '3px',
    marginBottom: '3px',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    borderRadius: theme.borderRadius.md,
    backgroundColor: ({ chipBg }) => chipBg,
    padding: ({ miniEvent, isXs }) =>
      isXs ? theme.spacing(0.25) : miniEvent ? theme.spacing(0, 1.25) : theme.spacing(1, 1.25),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: ({ miniEvent, isXs }) => (!isXs && miniEvent ? 'center' : 'flex-start'),
    cursor: 'pointer',
    transition: 'background-color 0.1s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ surface }) => surface
      }
    },
    [theme.breakpoints.down('sm')]: {
      '& p': {
        fontSize: theme.typography.pxToRem(12) + ' !important'
      }
    }
  },
  headerRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(1.25)
  },
  contentColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1
  },
  accentPill: {
    width: '3px',
    alignSelf: 'stretch',
    borderRadius: '5px',
    backgroundColor: ({ accentSoft }) => accentSoft,
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  titleLabel: {
    color: ({ accentSoft }) => accentSoft
  },
  timeLabel: {
    color: ({ accentSoft }) => accentSoft
  },
  unavailable: {
    backgroundColor: theme.paintscout.palette?.brand?.Neutral?.P94,
    backgroundImage: getUnavailableStripeBackground(theme),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'visible',
    zIndex: 999,
    cursor: 'default',
    '&::after': unavailableBlurOverlay,
    '&:hover $unavailableText': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  unavailableText: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
    color: theme.palette.common.white,
    padding: theme.spacing(1, 2),
    borderRadius: theme.borderRadius.md,
    zIndex: 2000,
    opacity: 1,
    pointerEvents: 'auto',
    transition: 'opacity 0.2s',
    cursor: 'default'
  },
  integrationOverlay: {
    position: 'relative',
    overflow: 'hidden',
    // Add a dashed overlay using a pseudo-element
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: theme.borderRadius.md,
      // Dashed pattern overlay, semi-transparent so text/background show through
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04) 0px,
          rgba(0,0,0,0.04) 10px,
          transparent 10px,
          transparent 13px
        )
      `
    }
  }
}))

export default function WeekEvent(props: EventProps<CalendarEvent>) {
  const isXs = useIsMobile({ xs: true })

  const { events: contextEvents, updateEvent, deleteEvent } = useCalendar()
  const { users } = useUsers()
  const { event } = props
  const { start, end, eventType } = event

  const ref = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [hoveredCell, setHoveredCell] = useState(false)
  const [hoveredPopup, setHoveredPopup] = useState(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const miniEvent = moment(end).diff(moment(start), 'minutes') === 30 || isXs
  const palette = getCrewPalette(event.estimator?.color)
  const classes = useStyles({ ...palette, miniEvent, isXs })

  const { openDialog, dismissDialog } = useDialogs()
  const cancelEventFlow = useCancelEventFlow()

  const handleEditEvent = () => {
    // Calendar.tsx shifts event.start/end so the time grid renders in company tz;
    // resolve back to the original UTC event so EventDialog's `.tz(timezone)`
    // formatting renders the real scheduled time.
    const originalEvent = contextEvents.find((evt) => evt.id === event.id) ?? event
    openDialog(EventDialog, {
      event: originalEvent,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissDialog()
        setAnchorEl(null)
      }
    })
  }

  const handleCancelEvent = (event: CalendarEvent) =>
    cancelEventFlow(event, (evt, template) => {
      deleteEvent(evt, template)
      setAnchorEl(null)
    })

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setAnchorEl(anchorEl ? null : e.currentTarget)
  }

  // Update rect on hover, scroll, and resize
  useEffect(() => {
    if (!(hoveredCell || hoveredPopup)) return
    function updateRect() {
      if (ref.current) setRect(ref.current.getBoundingClientRect())
    }
    updateRect()
    window.addEventListener('scroll', updateRect, true)
    window.addEventListener('resize', updateRect)
    return () => {
      window.removeEventListener('scroll', updateRect, true)
      window.removeEventListener('resize', updateRect)
    }
  }, [hoveredCell, hoveredPopup])

  if (eventType === '_unavailable_') {
    const showPopup = hoveredCell || hoveredPopup

    const calendar = document.querySelector('.rbc-time-content') as HTMLElement
    const calendarRect = calendar?.getBoundingClientRect()
    const eventRect = ref.current?.getBoundingClientRect()

    let left = eventRect ? eventRect.left + eventRect.width / 2 : 0
    let top = eventRect ? eventRect.top + eventRect.height / 2 : 0

    // Clamp left and top to calendar bounds
    left = Math.max(calendarRect?.left + 24, Math.min(left, calendarRect?.right - 24))
    top = Math.max(calendarRect?.top + 24, Math.min(top, calendarRect?.bottom - 24))

    return (
      <>
        <div
          ref={ref}
          className={classnames(classes.root, classes.unavailable, 'rbc-event-unavailable')}
          style={{ minHeight: 40 }}
          onClick={() => {
            setHoveredCell(true)
            setTimeout(() => {
              setHoveredCell(false)
            }, 1000) // Allow tap to register
          }} // Add this for mobile tap
          onMouseEnter={() => setHoveredCell(true)}
          onMouseLeave={() => setHoveredCell(false)}
        />
        {showPopup &&
          rect &&
          ReactDOM.createPortal(
            <div
              className={classes.unavailableText}
              style={{
                position: 'fixed',
                left,
                top,
                transform: 'translate(-50%, -50%)',
                opacity: 1,
                pointerEvents: 'auto',
                zIndex: 2000
              }}
              onMouseEnter={() => setHoveredPopup(true)}
              onMouseLeave={() => setHoveredPopup(false)}
            >
              <Typography variant="body2" weight={500}>
                Unavailable
              </Typography>
            </div>,
            document.body
          )}
      </>
    )
  }

  const WeekEventContent = () => {
    if (event.allDay) {
      return (
        <Typography
          variant="body2"
          weight={600}
          noWrap
          className={classes.titleLabel}
          style={{ position: 'relative', zIndex: 2, minWidth: 0, flex: 1 }}
        >
          {event.title || (event.contact ? formatName(event.contact) : 'Event')}
        </Typography>
      )
    }
    if (event.eventType === GOOGLE_CALENDAR_EVENT_TYPE) {
      const title = event.title || 'Event'
      return (
        <div className={classes.contentColumn}>
          <Typography
            variant="body2"
            weight={600}
            noWrap
            className={classes.titleLabel}
            style={{ position: 'relative', zIndex: 2 }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            className={classes.timeLabel}
            style={{ position: 'relative', zIndex: 2, fontSize: 12 }}
          >
            {!miniEvent && getEventLabel(moment(event.start), moment(event.end), false)}
          </Typography>
        </div>
      )
    } else {
      return (
        <div className={classes.contentColumn}>
          {event.contact && (
            <Typography variant="body2" weight={600} noWrap className={classes.titleLabel}>
              {formatName(event.contact)}
            </Typography>
          )}
          {!miniEvent && (
            <Typography variant="body2" className={classes.timeLabel} style={{ fontSize: 12 }}>
              {getEventLabel(moment(event.start), moment(event.end), event.allDay)}
            </Typography>
          )}
        </div>
      )
    }
  }

  return (
    <>
      <div
        className={classnames(
          classes.root,
          event.eventType === GOOGLE_CALENDAR_EVENT_TYPE ? classes.integrationOverlay : null,
          event.eventType === GOOGLE_CALENDAR_EVENT_TYPE ? 'rbc-event-imported' : null
        )}
        onClick={handleClick}
      >
        <div className={classes.headerRow}>
          <span className={classes.accentPill} />
          <WeekEventContent />
        </div>
      </div>
      <EventPreviewPopper
        event={contextEvents.find((evt) => evt.id === event.id) ?? event}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        users={users}
        onClose={() => setAnchorEl(null)}
        onEditEvent={handleEditEvent}
        onCancelEvent={handleCancelEvent}
        onNavigate={(path) => history.push(path)}
        minWidth={330}
      />
    </>
  )
}
